/* Career map · model helpers: lookups, stage tones, icons by label. */
import {
  BadgeCheck, Compass, Users, Lightbulb, GraduationCap, HandHeart, CalendarCheck, Flag, Sparkles,
  Telescope, Wrench, ArrowUpRight, Circle,
} from 'lucide-react';
import { LT } from '../../contexts/AppCatalogueContext';

export const cellFor = (row, n) => row.cells.find(c => c.levels.includes(n));
export const stageFor = (map, n) => map.stages.find(s => s.levels.includes(n));
// Stages darken as the career advances · one hue, light to dark (it is a
// progression, not four categories). `ink` keeps text readable on each.
export const STAGE_TONES = [
  { fill: '#E6ECE7', ink: LT.greenDeep, soft: '#F2F5F2' },
  { fill: '#BCCCC3', ink: LT.greenDeep, soft: '#E4EBE6' },
  { fill: '#7C9890', ink: '#FBFBFA',    soft: '#D5DFD9' },
  { fill: '#2B4138', ink: '#FBFBFA',    soft: '#C9D4CE' },
];
export const toneFor = (map, n) => {
  const i = map.stages.findIndex(s => s.levels.includes(n));
  return STAGE_TONES[Math.max(0, Math.min(STAGE_TONES.length - 1, Math.round((i / Math.max(1, map.stages.length - 1)) * (STAGE_TONES.length - 1))))];
};

// One icon per key competency, matched on the French name.
const COMP_ICONS = [
  [/qualit/i, BadgeCheck], [/autonom/i, Compass], [/équipe|equipe/i, Users], [/initiative/i, Lightbulb],
  [/apprentissage/i, GraduationCap], [/transmission/i, HandHeart], [/organisation/i, CalendarCheck], [/leadership/i, Flag],
];
export const compIcon = (name) => (COMP_ICONS.find(([re]) => re.test(name || '')) || [null, Sparkles])[1];

// The keywords row ("Mots-clés") is shown as each level's tagline · found on
// the French labels, so it works whatever language the page is in.
export const keywordRow = (canon) => (canon?.rows || []).findIndex(r => /^mots/i.test(r.label || ''));


// The topic rows (Profil, Compétences, Management, Pour passer…) get a
// Lucide icon from their French label · the Excel's emoji stay out of the UI.
const ROW_ICONS = [
  [/profil/i, Telescope], [/comp[ée]tence|technique|m[ée]tier/i, Wrench],
  [/management|encadrement/i, Users], [/passer|suivant/i, ArrowUpRight], [/mots/i, Sparkles],
];
export const rowIcon = (label) => (ROW_ICONS.find(([re]) => re.test(label || '')) || [null, Circle])[1];


/* Splits a text on its "━━ VOIE EXPERT ━━" / "━━ VOIE MANAGER ━━" markers ·
 * the dual track from level 6, shown as two lanes side by side. */
export function splitTracks(text) {
  const lines = (text || '').split('\n');
  const marks = lines.map((l, i) => (/^\s*━+.*━+\s*$/.test(l) ? i : -1)).filter(i => i >= 0);
  if (marks.length < 2) return null;
  return {
    intro: lines.slice(0, marks[0]).join('\n'),
    tracks: marks.map((m, k) => ({
      title: lines[m].replace(/━+/g, '').trim(),
      body: lines.slice(m + 1, marks[k + 1] ?? lines.length).join('\n'),
    })),
  };
}


// The row a topic is about, from its French label (used on the canonical
// data, so it works in every language).
export const rowIndex = (canon, re) => (canon?.rows || []).findIndex(r => re.test(r.label || ''));
export const NEXT_RE = /passer|suivant/i;
export const MGMT_RE = /management|encadrement/i;

/* Where the model branches: the first level whose management text offers
 * the optional manager track, and the first one split into two tracks. */
export function branches(canon) {
  const mi = rowIndex(canon, MGMT_RE);
  if (mi < 0) return { managerFrom: null, forkAt: null };
  const row = canon.rows[mi];
  let managerFrom = null;
  let forkAt = null;
  for (const l of canon.levels || []) {
    const c = cellFor(row, l.n);
    if (!c) continue;
    if (managerFrom == null && /piste manager/i.test(c.text)) managerFrom = l.n;
    if (forkAt == null && (splitTracks(c.text) || /deux voies/i.test(c.text))) forkAt = l.n;
  }
  return { managerFrom, forkAt };
}
